var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import mapboxgl from "mapbox-gl";
import { CommonActions, CommonPlaceholders, CommonMessages } from "../../localization/helpers.js";
let MapComponent = class MapComponent extends LitElement {
    constructor() {
        super(...arguments);
        this.searchOpen = false;
        this.geojson = null;
        this.selectedPoi = null;
        this.mapboxApiKey = null;
        this.tempMarker = null;
    }
    firstUpdated() {
        // Add Mapbox CSS via <link>
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css";
        this.shadowRoot.appendChild(link);
        mapboxgl.accessToken = this.mapboxApiKey;
        this.map = new mapboxgl.Map({
            container: this.shadowRoot.getElementById("map"),
            // style: "mapbox://styles/mapbox/satellite-v9",
            center: [2.2137, 46.2276],
            zoom: 5,
            projection: "globe", // Enable globe projection for zoomed-out views
        });
        this.map.on("style.load", () => {
            // Add terrain with exaggeration
            this.map.addSource("mapbox-dem", {
                type: "raster-dem",
                url: "mapbox://mapbox.mapbox-terrain-dem-v1",
                tileSize: 512,
                maxzoom: 14,
            });
            // this.map.setConfigProperty('basemap', 'lightPreset', 'day');
            this.map.setConfigProperty('basemap', 'showPointOfInterestLabels', false);
            this.map.setTerrain({ source: "mapbox-dem" });
            // Ensure the globe remains visible when zoomed out
            this.map.setProjection("globe");
            // Optional: Adjust lighting for better terrain visualization
            this.map.setLight({
                anchor: "viewport",
                intensity: 0.8,
            });
        });
        this.map.on("load", () => {
            if (this.geojson)
                this.addClusterLayer();
        });
        // Ajout d'un gestionnaire pour les clics sur la carte
        this.map.on("click", (event) => {
            const { lng, lat } = event.lngLat;
            this.dispatchEvent(new CustomEvent("click-map", {
                detail: { longitude: lng, latitude: lat },
                bubbles: true,
                composed: true,
            }));
        });
    }
    updated(changedProps) {
        if (changedProps.has("geojson") && this.geojson && this.map?.isStyleLoaded()) {
            const source = this.map.getSource("points");
            if (source) {
                source.setData(this.geojson);
            }
            else {
                this.addClusterLayer();
            }
            this.flyToGeoJsonBounds();
        }
        if (changedProps.has("selectedPoi")) {
            if (this.selectedPoi) {
                this.flyToPoi(this.selectedPoi);
                this.addSelectedPoiMarker();
            }
            else {
                this.removeSelectedPoiMarker();
                this.flyToGeoJsonBounds();
            }
        }
    }
    flyToGeoJsonBounds() {
        if (this.geojson && this.geojson.bbox) {
            const [minLng, minLat, maxLng, maxLat] = this.geojson.bbox;
            this.map.fitBounds([
                [minLng, minLat],
                [maxLng, maxLat],
            ], { padding: 40, animate: true });
        }
    }
    createPopupContent(properties) {
        return `
    <div style="
      font-size: 14px;
      color: #000; 
      background-color: #ffffff; 
      padding: 15px; 
      border-radius: 8px; 
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      max-width: 250px;
    ">
      <h3 style="
        margin: 0 0 8px 0;
        font-size: 18px;
        font-weight: bold;
      ">
        ${properties.name || msg('No name available', { id: 'map.noName', desc: 'No name available' })}
      </h3>
      <p style="
        margin: 0 0 12px 0;
        font-size: 14px;
        line-height: 1.4;
        color: #333;
      ">
        ${properties.description || CommonMessages.noDescription()}
      </p>
      <ul style="
        list-style: none; 
        padding: 0; 
        margin: 0 0 12px 0;
        font-size: 13px;
        color: #555;
      ">
        <li><strong>poi_id:</strong> ${properties.poi_id || "N/A"}</li>
        <li><strong>id:</strong> ${properties.id || "N/A"}</li>
      </ul>
      <div style="display: flex; gap: 8px;">
        <button id="update-btn" style="
          flex: 1;
          padding: 6px 12px;
          background-color: #007BFF;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          transition: background-color 0.2s;
        " onmouseover="this.style.backgroundColor='#0056b3'" onmouseout="this.style.backgroundColor='#007BFF'">
          ${CommonActions.edit()}
        </button>
        <button id="delete-btn" style="
          flex: 1;
          padding: 6px 12px;
          background-color: #FF4C4C;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          transition: background-color 0.2s;
        " onmouseover="this.style.backgroundColor='#cc0000'" onmouseout="this.style.backgroundColor='#FF4C4C'">
          ${CommonActions.delete()}
        </button>
      </div>
    </div>
  `;
    }
    addClusterLayer() {
        if (!this.geojson)
            return;
        if (this.map.getSource("points"))
            return; // éviter duplication
        this.map.addSource("points", {
            type: "geojson",
            data: this.geojson,
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50,
        });
        // Cercle clusters
        this.map.addLayer({
            id: "clusters",
            type: "circle",
            source: "points",
            filter: ["has", "point_count"],
            paint: {
                "circle-color": [
                    "step",
                    ["get", "point_count"],
                    "#51bbd6",
                    100,
                    "#f1f075",
                    750,
                    "#f28cb1",
                ],
                "circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40],
            },
        });
        // Label nombre de points
        this.map.addLayer({
            id: "cluster-count",
            type: "symbol",
            source: "points",
            filter: ["has", "point_count"],
            layout: {
                "text-field": "{point_count_abbreviated}",
                "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
                "text-size": 12,
            },
        });
        // Points non clusterisés
        this.map.addLayer({
            id: "unclustered-point",
            type: "circle",
            source: "points",
            filter: ["!", ["has", "point_count"]],
            paint: {
                "circle-color": "#11b4da",
                "circle-radius": 6,
                "circle-stroke-width": 1,
                "circle-stroke-color": "#fff",
            },
        });
        // Zoom au clic sur cluster
        this.map.on("click", "clusters", (e) => {
            const features = this.map.queryRenderedFeatures(e.point, {
                layers: ["clusters"],
            });
            if (!features.length)
                return;
            const clusterId = features[0].properties?.cluster_id;
            this.map.getSource("points").getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err || zoom == null)
                    return;
                this.map.easeTo({
                    center: features[0].geometry.coordinates,
                    zoom,
                });
            });
        });
        // Popup points non clusterisés
        this.map.on("click", "unclustered-point", (e) => {
            if (!e.features || e.features.length === 0)
                return;
            const feature = e.features[0];
            if (!feature.geometry || feature.geometry.type !== "Point")
                return;
            const coordinates = feature.geometry.coordinates.slice();
            const props = feature.properties || {};
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }
            const popup = new mapboxgl.Popup()
                .setLngLat(coordinates)
                .setHTML(this.createPopupContent(props))
                .addTo(this.map);
            // Add event listener for the "Update" button
            const updateButton = popup.getElement()?.querySelector("#update-btn");
            if (updateButton) {
                updateButton.addEventListener("click", () => {
                    // Close popup
                    popup.remove();
                    // Dispatch event to open edit modal
                    this.dispatchEvent(new CustomEvent("edit-poi-form", {
                        detail: { poiId: props.poi_id },
                        bubbles: true,
                        composed: true,
                    }));
                });
            }
            // Add event listener for the "Supprimer" button
            const deleteButton = popup.getElement()?.querySelector("#delete-btn");
            if (deleteButton) {
                deleteButton.addEventListener("click", () => {
                    // Close the popup
                    popup.remove();
                    this.dispatchEvent(new CustomEvent("delete-poi", {
                        detail: { poiId: props.poi_id },
                        bubbles: true,
                        composed: true,
                    }));
                });
            }
        });
        // Curseur interactif
        this.map.on("mouseenter", "clusters", () => (this.map.getCanvas().style.cursor = "pointer"));
        this.map.on("mouseleave", "clusters", () => (this.map.getCanvas().style.cursor = ""));
    }
    async toggleSearch() {
        this.searchOpen = !this.searchOpen;
        if (this.searchOpen) {
            await this.updateComplete;
            const input = this.shadowRoot.getElementById("search-input");
            input.focus();
        }
    }
    handleAddTempPoi(event) {
        const detail = event.detail;
        if (detail && detail.longitude !== undefined && detail.latitude !== undefined) {
            // Remove existing temp marker if any
            this.handleCleanTempPoi();
            // Create a new basic marker
            this.tempMarker = new mapboxgl.Marker({ color: "red" })
                .setLngLat([detail.longitude, detail.latitude])
                .addTo(this.map);
            // Zoom to the position
            this.map.flyTo({ center: [detail.longitude, detail.latitude], zoom: 14 });
        }
    }
    handleCleanTempPoi() {
        if (this.tempMarker) {
            this.tempMarker.remove();
            this.tempMarker = null;
        }
    }
    handleSearch(e) {
        e.preventDefault();
        const input = this.shadowRoot.getElementById("search-input");
        const query = input.value.trim();
        if (!query)
            return;
        fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${this.mapboxApiKey}`)
            .then((res) => res.json())
            .then((data) => {
            if (data.features && data.features.length > 0) {
                const [lng, lat] = data.features[0].geometry.coordinates;
                this.map.flyTo({ center: [lng, lat], zoom: 14 });
                new mapboxgl.Marker().setLngLat([lng, lat]).addTo(this.map);
            }
            else {
                alert(msg('Place not found', { id: 'map.search.notFound', desc: 'Alert when a searched place is not found' }));
            }
        });
        this.searchOpen = false;
        input.value = "";
    }
    zoomIn() {
        this.map.zoomIn();
    }
    zoomOut() {
        this.map.zoomOut();
    }
    locate() {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((pos) => {
                const lng = pos.coords.longitude;
                const lat = pos.coords.latitude;
                this.map.flyTo({ center: [lng, lat], zoom: 14 });
                new mapboxgl.Marker().setLngLat([lng, lat]).addTo(this.map);
            });
        }
    }
    flyToPoi(selectedPoi) {
        if (!this.geojson || !selectedPoi)
            return;
        const poi = this.geojson.features.find((feature) => feature.properties?.poi_id === selectedPoi.poi_id);
        if (poi && poi.geometry.type === "Point") {
            const [lng, lat] = poi.geometry.coordinates;
            this.map.flyTo({
                center: [lng, lat],
                zoom: 17, // Zoom plus rapproché
                bearing: 0, // Orientation nord
                pitch: 0, // Inclinaison pour une vue en perspective
            });
        }
        else {
            console.warn("POI non trouvé ou type de géométrie invalide.");
        }
    }
    addSelectedPoiMarker() {
        if (!this.geojson || !this.selectedPoi)
            return;
        const poi = this.geojson.features.find((feature) => feature.properties?.poi_id === this.selectedPoi.poi_id);
        if (poi && poi.geometry.type === "Point") {
            const [lng, lat] = poi.geometry.coordinates;
            // Delete existing marker if any
            this.removeSelectedPoiMarker();
            // Ajouter un marqueur pour le POI sélectionné uniquement si aucun n'existe
            this.tempMarker = new mapboxgl.Marker({ color: "blue" })
                .setLngLat([lng, lat])
                .addTo(this.map);
        }
        else {
            console.warn("POI non trouvé ou type de géométrie invalide.");
        }
    }
    removeSelectedPoiMarker() {
        if (this.tempMarker) {
            this.tempMarker.remove();
            this.tempMarker = null;
        }
    }
    render() {
        return html `
      <div id="map"></div>
      <div class="controls">
        <form
          class="search-btn ${this.searchOpen ? "open" : ""}"
          @submit=${this.handleSearch}
        >
          <input
            id="search-input"
            type="text"
            placeholder=${CommonPlaceholders.search()}
            @keydown=${(e) => e.stopPropagation()}
          />
          <button type="button" @click=${this.toggleSearch} title=${msg('Search', { id: 'map.search', desc: 'Search button title' })}>
            <img src="search.svg" alt=${msg('Search', { id: 'map.search.alt', desc: 'Alt text for search icon' })} />
          </button>
        </form>
        <button class="control-btn" @click=${this.zoomIn} title=${msg('Zoom In', { id: 'map.zoomIn', desc: 'Zoom in button title' })}>
          <img src="plus.svg" alt=${msg('Zoom In', { id: 'map.zoomIn.alt', desc: 'Alt text for zoom in icon' })} />
        </button>
        <button class="control-btn" @click=${this.zoomOut} title=${msg('Zoom Out', { id: 'map.zoomOut', desc: 'Zoom out button title' })}>
          <img src="minus.svg" alt=${msg('Zoom Out', { id: 'map.zoomOut.alt', desc: 'Alt text for zoom out icon' })} />
        </button>
        <button class="control-btn" @click=${this.locate} title=${msg('My Location', { id: 'map.myLocation', desc: 'My location button title' })}>
          <img src="loc.svg" alt=${msg('Locate Me', { id: 'map.locate.alt', desc: 'Alt text for locate me icon' })} />
        </button>
      </div>
    `;
    }
};
MapComponent.styles = css `
    :host {
      display: block;
      height: 100%;
      width: 100%;
      position: relative;
      font-family: inherit;
    }
    #map {
      height: 100%;
      width: 100%;
    }
    .controls {
      position: absolute;
      top: 50%;
      right: 10px;
      transform: translateY(-50%);
      display: flex;
      flex-direction: column;
      gap: 10px;
      z-index: 10;
      align-items: flex-end;
    }
    .search-btn {
      display: flex;
      align-items: center;
      width: 36px;
      height: 36px;
      border-radius: 50px;
      overflow: hidden;
      background: rgba(255, 255, 255, 0.8);
      cursor: pointer;
      transition: width 0.3s ease;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    }
    .search-btn.open {
      width: 200px;
    }
    .search-btn input {
      flex: 1;
      border: none;
      outline: none;
      padding: 0 8px;
      display: none;
      background: transparent;
      color: black;
      font-size: 14px;
    }
    .search-btn.open input {
      display: block;
    }
    .search-btn button {
      width: 36px;
      height: 36px;
      border: none;
      background: transparent;
      font-size: 18px;
      cursor: pointer;
    }
    button.control-btn {
      width: 36px;
      height: 36px;
      border: none;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 18px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
      transition: background 0.2s;
    }
    button.control-btn:hover {
      background: rgba(255, 255, 255, 1);
    }
    button.control-btn img {
      width: 18px;
      height: 18px;
    }
    .search-btn img {
      width: 18px;
      height: 18px;
    }
  `;
__decorate([
    state()
], MapComponent.prototype, "searchOpen", void 0);
__decorate([
    property({ type: Object })
], MapComponent.prototype, "geojson", void 0);
__decorate([
    property({ type: Number })
], MapComponent.prototype, "selectedPoi", void 0);
__decorate([
    property({ type: Object })
], MapComponent.prototype, "mapboxApiKey", void 0);
MapComponent = __decorate([
    localized(),
    customElement("map-component")
], MapComponent);
export { MapComponent };
//# sourceMappingURL=MapComponent.js.map